import firebase from "firebase/compat/app";
import "firebase/compat/firestore";
import { RGBAColor } from "../Common";
import { Customer, CustomerProfileConfiguration } from "../customer";
import { GiftCertificate } from "../GiftCertificate";
import { PaymentTypeId, Transaction } from "../Payments/transaction";
import { Product, ProductGroup } from "../product";
import { RewardStampCardData, StampCardData, StampCardSetups } from "../StampCard";
import { EmailBrandingTemplates, VoucherData, VoucherProduct, VoucherSetups } from "../Voucher";
import { MembershipAssetType, MembershipEvent } from "./MembershipEvent";
import { MembershipRewardInfoBase } from "./MembershipRewardInfoRequest";
import { MembershipTier } from "./MembershipTier";
import { FeatureFlagMap } from "../Menu";

export enum MembershipDuration {
  Monthly = "monthly",
  Annual = "annual",
  Lifetime = "lifetime",
}

// Status of the membership
export enum MembershipStatus {
  Active = "active",
  Suspended = "suspended",
  Expired = "expired",
  Cancelled = "cancelled",
  Pending = "pending",
}

export interface CustomerGroups {
  [id: string]: CustomerGroup;
}

export interface MembershipTierColors {
  background: RGBAColor; // card background
  foreground?: RGBAColor; // card foreground
  label: RGBAColor; // labels eg Membership Number
  text: RGBAColor; // data values eg Gold
  tier: RGBAColor; // tier color to denote tier
  title?: RGBAColor; // card title
}

export interface CustomerGroup {
  id?: string;
  _key?: string;
  name: string; // eg Gold | Platinum
  allocationPriority: number;
  colors: MembershipTierColors;
  bgImage: string; // bg image used in membership home | hub page
  description: string;
  enabled: boolean;
  tier: MembershipTier;
  duration: MembershipDuration;
  restaurantId?: string; // null value denotes a universal group
  default?: boolean; // denotes if this is default
  rewardMultiplier: number; // Earnng percentage eg 5% = 0.05
  rewardTTL: number; // how long (in months) this reward is valid for from creation date
  // widgetId: string; // each tier has its own mapped widget
  // priceAdjustmentConfig: Omit<PaymentPriceAdjustmentPayload, "_key" | "orderItemId" | "accountGroupName" | "accountProductName" | "accountGroupAdjustmentType" | "change" | "type"> & {
  //   productTree: ProductCategoryId;
  // };
}

// definition for a member who is linked to "primary" membership
export interface LinkedMember {
  customerId: string;
  relationship?: "spouse" | "child" | "parent" | "sibling" | "associate" | "other";
  /** Percentage of points this member can redeem (0-100) */
  pointsRedemptionAllowance: number;
  dateAdded: number; // Date.now()
  isActive: boolean;
}

/**
 * Primary membership def
 */
export interface Membership {
  id: string;
  membershipNumber: string; // use generateMembershipNumber() to 7 digits, unique
  customerId: string; // aka primary account holder
  mobile: string; // for fast db lookup when customerId is not known
  enabled: boolean;
  restaurantId: string;
  customerGroupId: string;
  duration: MembershipDuration;
  status: MembershipStatus;
  dateJoined: number;
  validFrom: number;
  durationMonths: number; // Duration in months until expiry: eg MONTHLY: 1, ANNUAL: 12 or LIFETIME: 0 (ie never expires)
  expiryDate: number | null;
  autoRenew: boolean;
  renewalCount: number;
  pointsBalance: number;
  totalPointsEarned: number; // Total points earned so far
  totalPointsRedeemed: number; // Total points redeemed over membership lifetime
  members: LinkedMember[]; // linked members family or corpoate
  lastActivityDate: number;
  promoCode?: string; // promotional code used at signup
  referredBy?: string; // referral information
}

/**
 * For creating a new membership
 */
export type CreateMembershipInput = Omit<Membership, "pointTransactions" | "totalPointsEarned" | "totalPointsRedeemed" | "renewalCount" | "lastActivityDate"> & {
  initialPoints?: number;
  initialLinkedMembers?: Omit<LinkedMember, "dateAdded">[];
};

/**
 * For updating an existing membership
 */
export type UpdateMembershipInput = Partial<Omit<Membership, "id" | "customerId" | "membershipNumber" | "dateJoined">>;

// Wallet Pass generation options
export interface WalletPassGenerationOptions {
  customFields?: {
    key: string;
    label: string;
    value: string;
  }[];
}

export type WalletPassConfigs = {
  [K in MembershipTier]: WalletPassConfig;
};

// Tier based Wallet configuration for restaurant
export interface WalletPassConfig {
  restaurantId: string;
  logoImageApple: string;
  logoImageGoogle?: string;
  colors: MembershipTierColors;
  options?: WalletPassGenerationOptions;
  tier: MembershipTier;
}

// Used for creating Wallet Pass (Apple and Google)
export interface MembershipPassData {
  membershipId: string;
  memberName: string;
  membershipNumber: string;
  membershipLevel: string;
  memberSince: string;
  restaurantId: string;
  organizationName: string;
}

export interface MembershipAppSettings {
  customerProfile?: CustomerProfileConfiguration;
  file?: {
    bucket: string;
    fullPath: string;
  };
  staticMapURL?: string;
  termsAndConditions?: string;
  useGroupWebsite?: boolean;
  customerGroups?: CustomerGroups;
  walletPass?: WalletPassConfigs;
  enableMembership: boolean; // if core membership feature is enabled (basically membership app)
  enablePoints: boolean; // if points related feature in membership app is enabled
  pointsLabel: string; // Cashback Dollar | LB Coin
  enableStampCards: boolean; // if stamp card related feature is enabled
  promotions?: MembershipPromotionItems; // banner | carourels
  homeScreen?: HomeScreenConfig;
  invitationSource?: MembershipInvitationSource[]; // list of enabled (available) invitation pathways to register as member
  /** hours before first reward can be redeemed after joining
   * in fute we can consider similar delays such as`repeatRedemptionDelayHours`, `tierUpgradeRedemptionDelayHours` ...   */
  firstRewardRedemptionDelayHours?: number;
  flags?: FeatureFlagMap<"showOnBookingHub">;
}

// List of PaymentTypeIds that are allowed to award membership points
export const SUPPORTED_PAYMENT_TYPE_FOR_POINT = [
  PaymentTypeId.Cash, //
  PaymentTypeId.CNP,
  PaymentTypeId.Offline,
  PaymentTypeId.Eftpos,
  PaymentTypeId.ManualBankTransfer,
  PaymentTypeId.ManualCreditCard,
  PaymentTypeId.BankTransfer,
] as const;

export type SupportedPaymentTypeForPoint = (typeof SUPPORTED_PAYMENT_TYPE_FOR_POINT)[number];

export type MembershipSearchResult = {
  [membershipId: string]: MembershipSearchResultItem;
};

export type MembershipSearchResultItem = {
  customer: Partial<Customer>;
  membership: Partial<Membership>;
  vouchers: Array<VoucherData>;
  rewardStampCards: Array<RewardStampCardData>;
  stampCards: Array<StampCardData>;
  giftCertificates?: Array<GiftCertificate>;
};

export type MembershipRewardInfo = MembershipRewardInfoBase & {
  assetType: MembershipAssetType.Voucher | MembershipAssetType.StampCard;
  rewardData: RewardStampCardData | VoucherData;
  rewardProducts: VoucherProduct[];
  orderItemId: string;
};

export interface MembershipTransactions {
  [id: string]: MembershipTransaction;
}

export interface MembershipTransaction {
  id: string; // this is transaction id
  transactionNumber: number;
  membershipId: string;
  pointsBalance: number;
  totalPointsEarned: number; // points earned in this transaction
  totalPointsRedeemed: number; // points redeemed in this transaction
  events: MembershipEvent[]; // list of events tied to this transactions: stamps earned | redeemed | points earned ....
  timeStamp: number;
}

export type MembershipPromotionItems = {
  [K in MembershipTier]: MembershipPromotionItem[];
};

// Tier based Promotional Asset configuration for restaurant
export interface MembershipPromotionItem {
  imageUrl: string;
  promoUrl: string;
  label?: string;
}
export interface TransactionData {
  id: string;
  transaction: Transaction;
  membersTransaction: MembershipTransaction;
}

export interface Memberships {
  [key: string]: Membership;
}

export interface MembershipAccountProductInfo {
  product: Product;
  productGroup: ProductGroup;
}
export interface MembershipAccountProducts {
  stampReward?: MembershipAccountProductInfo;
  cashback?: MembershipAccountProductInfo;
  giftCertificate?: MembershipAccountProductInfo;
  voucher?: MembershipAccountProductInfo;
}
export interface MembershipSettings {
  appSettings: MembershipAppSettings;
  customerGroups: CustomerGroups;
  accountProducts: MembershipAccountProducts;
  emailBrandingTemplates: EmailBrandingTemplates;
}

export interface HomeScreenConfig {
  icon: string;
  fullName: string;
  shortName: string;
  color: RGBAColor | string;
  manifest?: HomeScreenManifest; // manifest object auto gen'd on save}
}

export interface HomeScreenManifest {
  name: string; // Full name of the app (used on splash screen)
  short_name: string; // Shorter name used under home screen icon
  start_url: string; // URL to load when app is launched from home screen
  display: "fullscreen" | "standalone" | "minimal-ui" | "browser"; // Controls browser UI
  orientation?: "any" | "natural" | "landscape" | "portrait" | string; // Default screen orientation
  background_color: string; // Background color for splash screen
  theme_color: string; // Theme color for browser UI (e.g. status bar)
  icons: ManifestImage[]; // Array of icons with sizes and types
  lang: string; // Default language
  dir?: "ltr" | "rtl" | "auto"; // Text direction

  // Android specific
  scope?: string;
  categories?: string[]; // Hints for app stores
  screenshots?: ManifestImage[]; // Used for app store previews
  description?: string;
  prefer_related_applications?: boolean; // Suggest app install from app store
  related_applications?: RelatedApplication[]; // Array of native apps
}

export interface ManifestImage {
  src: string;
  sizes: string; // e.g., "192x192"
  type: string; // e.g., "image/png"
  purpose?: "any" | "maskable" | "monochrome"; // spec-compliant
}

export interface RelatedApplication {
  platform: string;
  url?: string;
  id?: string;
}

export type MembershipMenuItem = { displayName: string; value: string; type: MembershipAssetType.StampCard | MembershipAssetType.Voucher; isBlocked: boolean };

export enum MembershipInvitationStatus {
  Pending = "Pending",
  Sent = "Sent",
  Accepted = "Accepted",
  Expired = "Expired",
  Cancelled = "Cancelled",
}

/**
 * Identifies the flow that triggers a membership invitation SMS.
 * Used to track invite origin and tailor messaging.
 */
export enum MembershipInvitationSource {
  // ── In venue / Operator assisted ──────────────────────────────────────────────────────────────
  /** Waiter-prompted at POS terminal during bill payment (incl. split bills) */
  Till = "till",
  /** Manually triggered by an Operator from the CRM portal */
  CrmPortal = "crm_portal",
  /** Invite embedded on a printed receipt */
  // PrintedReceipt = "printed_receipt",

  // ── Self checkout flows (via ordering app) ─────────────────────────────────────────────────
  /** Memnu Plan pre service checkout  */
  PreServiceCheckout = "pre_service_checkout",
  /** Standard in service checkout  */
  InServiceCheckout = "in_service_checkout",
  /** Takeaway / pickup order */
  TakeawayCheckout = "takeaway_checkout",
  /** Purchase related to Function booking, eg payment for Bar tab */
  // FunctionBookingPayment = "function_booking_payment",
  /** Purchase related to Booking, eg deposit payment for Pre-paid menu */
  BookingPayment = "booking_payment",
  /** Gift certificate purchase */
  // GiftCertificatePayment = "gift_certificate_payment",
  // ── marketing Emails───────────────────────────────────────────────────────
  /** Newsletter email, send to bulk customers */
  // NewsletterEmail = "newsletter_email",
  /** Email that contains vouchers , eg Birthday voucher email */
  // VoucherEmail = "voucher_email",
  /** Generic email sent to specific cohorts, eg Customer feedback email
   * this can be elaborated into its own member like NewsletterEmail and VoucherEmail above  */
  // Email = "email",
}

export const MembershipInvitationSourceLabel: Record<MembershipInvitationSource, string> = {
  [MembershipInvitationSource.Till]: "Waiter-prompted at Till during bill payment (incl. split bills)",
  [MembershipInvitationSource.CrmPortal]: "Manually triggered by an Operator from the CRM portal",
  [MembershipInvitationSource.PreServiceCheckout]: "Upon completion of checkout payment for Menu Plan orders",
  [MembershipInvitationSource.InServiceCheckout]: "Upon completion of checkout payment for in-service orders",
  [MembershipInvitationSource.TakeawayCheckout]: "Upon completion of checkout payment for Takeaway orders",
  [MembershipInvitationSource.BookingPayment]: "Purchase related to Booking, eg deposit payment for Pre-paid menu",
  /**
   [MembershipInvitationSource.PrintedReceipt]: "Invite QR Code embedded on a printed receipt",
   [MembershipInvitationSource.QrCheckout]: "Upon completion of checkout payment on QR ordering",
   [MembershipInvitationSource.FunctionBookingPayment]: "Purchase related to Function booking, eg payment for Bar tab",
   [MembershipInvitationSource.GiftCertificatePayment]: "Upon Gift certificate purchase",
  [MembershipInvitationSource.NewsletterEmail]: "Via link in Newsletter email",
  [MembershipInvitationSource.VoucherEmail]: "Via link in an email that contains vouchers, eg Birthday voucher email",
  [MembershipInvitationSource.Email]: "Via link in generic email, eg Customer feedback email",
  **/
};

export interface MembershipInvitation {
  id: string;
  tenantId: string;
  restaurantId: string;
  mobile: string; // for fast search
  customerId?: string; // because user may not yet be a customer
  customerGroupId?: string; // only specify if the invitation is for specific tier
  status: MembershipInvitationStatus;
  createdAt: firebase.firestore.Timestamp;
  expiresAt: firebase.firestore.Timestamp; // set this to 7 days
  isEnabled: boolean;
  acceptedAt?: firebase.firestore.Timestamp; // When customer accepted the invite
  lastReminderSentAt?: firebase.firestore.Timestamp;
  requestHistory: MembershipInvitationRequest[];
  cloudTaskName?: string; // V2 Cloud Task name for delayed invitation
}

export type OperatorMembershipInvitationSource =
  | MembershipInvitationSource.Till //
  | MembershipInvitationSource.CrmPortal;

export type CustomerMembershipInvitationSource =
  | MembershipInvitationSource.InServiceCheckout //
  | MembershipInvitationSource.PreServiceCheckout
  | MembershipInvitationSource.TakeawayCheckout
  | MembershipInvitationSource.BookingPayment;


interface BasseMembershipInvitationRequest {
  restaurantId: string;
  /** optional or derived */
  operatorId?: string;
  mobile?: string;
  createdAt?: firebase.firestore.Timestamp;
  transactionIds?: [];
  amount?: number;
  operatorName?: string;
  customerId?: string;
  sendLater?: boolean;
}
export interface OperatorMembershipInvitationRequest extends BasseMembershipInvitationRequest {
  source: OperatorMembershipInvitationSource;
  amount: number;
  operatorId: string;
}

export interface CustomerMembershipInvitationRequest extends BasseMembershipInvitationRequest{
  source: CustomerMembershipInvitationSource;
  sendLater: boolean;
  customerId: string;
}

export type MembershipInvitationRequest = OperatorMembershipInvitationRequest | CustomerMembershipInvitationRequest;

export interface MembershipOnboardingRewardsInfo {
  tier: CustomerGroup;
  claimableCashbackAmount: number;
  stampCards: StampCardSetups;
  vouchers: VoucherSetups;
}

export interface MembershipInvitationRewardsInfo extends MembershipOnboardingRewardsInfo {
  invitation: MembershipInvitation;
  hasExpired: boolean;
  expiresOn: string;
}
